/**
 * Approval / decline actions performed by approvers + admins.
 */
import { supabase } from '@/lib/supabase/client'
import { ConflictError, OpenDeclineCommentsError } from './errors'

export async function approveRecord(
  recordId: string,
  userId: string,
  userRole: string,
  projectId: string,
  comment?: string,
) {
  const { data: rec, error: recErr } = await supabase
    .from('records')
    .select('version')
    .eq('id', recordId)
    .single()
  if (recErr) throw recErr

  const { data, error } = await supabase.rpc('approve_record', {
    p_record_id: recordId,
    p_approver_id: userId,
    p_approver_role: userRole,
    p_project_id: projectId,
    p_expected_version: rec.version,
    p_comment: comment ?? null,
  })
  if (error) throw error

  const result = data as {
    success?: boolean
    error?: string
    current_version?: number
    current_status?: string
    count?: number
  }

  if (result?.error === 'conflict') {
    throw new ConflictError(result.current_version ?? 0, result.current_status ?? 'unknown')
  }
  if (result?.error === 'open_decline_comments') {
    throw new OpenDeclineCommentsError(result.count ?? 0)
  }
  if (result?.error) {
    throw new Error(result.error)
  }

  // Notifications are emitted server-side by the records status-change
  // trigger (migration 20260718120000) — nothing to send from here.
  void userRole
}

/**
 * Admin-only explicit reopen of an approved / needs_revision record back to
 * in_progress. Works regardless of edit_after_approval config.
 */
export async function reopenRecord(
  recordId: string,
  userId: string,
  projectId: string,
  reason?: string,
) {
  const { data: rec, error: recErr } = await supabase
    .from('records')
    .select('version')
    .eq('id', recordId)
    .single()
  if (recErr) throw recErr

  const { data, error } = await supabase.rpc('reopen_record', {
    p_record_id: recordId,
    p_user_id: userId,
    p_project_id: projectId,
    p_expected_version: rec.version,
    p_reason: reason ?? null,
  })
  if (error) throw error
  const result = data as { success?: boolean; error?: string; current_version?: number; current_status?: string }
  if (result?.error === 'conflict') {
    throw new ConflictError(result.current_version ?? 0, result.current_status ?? 'unknown')
  }
  if (result?.error === 'forbidden') throw new Error('فقط مدیر پروژه می‌تواند سابقه را بازگشایی کند')
  if (result?.error === 'invalid_status') throw new Error('این سابقه قابل بازگشایی نیست')
  if (result?.error) throw new Error(result.error)
}

export async function declineRecord(
  recordId: string,
  userId: string,
  userRole: string,
  projectId: string,
  comment: string,
) {
  const { data: rec, error: recErr } = await supabase
    .from('records')
    .select('version')
    .eq('id', recordId)
    .single()
  if (recErr) throw recErr

  const { data, error } = await supabase.rpc('decline_record', {
    p_record_id: recordId,
    p_approver_id: userId,
    p_approver_role: userRole,
    p_project_id: projectId,
    p_expected_version: rec.version,
    p_comment: comment,
  })
  if (error) throw error

  const result = data as {
    success?: boolean
    error?: string
    current_version?: number
    current_status?: string
  }

  if (result?.error === 'conflict') {
    throw new ConflictError(result.current_version ?? 0, result.current_status ?? 'unknown')
  }
  if (result?.error === 'comment_required') {
    throw new Error('دلیل رد الزامی است')
  }
  if (result?.error) {
    throw new Error(result.error)
  }

  // Notifications are emitted server-side by the records status-change
  // trigger (migration 20260718120000) — nothing to send from here.
  void userRole
}
